<script lang="ts">
/**
 * 首页 KPI 卡定义。
 *
 * value 由各 app 负责格式化后传进来（ops 的待结算金额要 toFixed(2)，
 * 加载中/取不到要显示什么也是各 app 的口径），本组件只管排版。
 */
export interface KpiCard {
  /** 稳定 key，v-for 用；也是「这次刷新变的是哪一张」的标识 */
  key: string
  /** 卡面下方的小字标签 */
  label: string
  /** 卡面上方的大字数值 */
  value: string | number
  /** 给了就整张卡可点，点击时 emit('select', to)；不给则纯展示 */
  to?: string
}
</script>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    cards: readonly KpiCard[]
    loading?: boolean
  }>(),
  { loading: false },
)

// 同 HomeQuickLinks：组件不引入 vue-router，跳转由宿主在 @select 里接。
const emit = defineEmits<{
  (e: 'select', to: string): void
}>()

function onClick(card: KpiCard): void {
  if (card.to) emit('select', card.to)
}
</script>

<template>
  <el-row v-loading="props.loading" :gutter="16" class="yc-kpi-grid">
    <el-col v-for="card in props.cards" :key="card.key" :xs="12" :sm="12" :md="6">
      <el-card
        class="yc-kpi-grid__card"
        :class="{ 'yc-kpi-grid__card--link': card.to }"
        @click="onClick(card)"
      >
        <!-- 插槽内容由宿主渲染，宿主自己的 scoped 样式对它生效（nos 的「查看」钻取就用这条路） -->
        <slot name="extra" :card="card" />
        <div class="yc-kpi-grid__value">{{ card.value }}</div>
        <div class="yc-kpi-grid__label">{{ card.label }}</div>
      </el-card>
    </el-col>
  </el-row>
</template>

<style scoped lang="scss">
.yc-kpi-grid {
  // el-row 自带 flex-wrap，卡片换行交给它。
  // 跨度必须给全 xs/sm/md 三档：el-col 的响应式类名带 @media（xs 无条件、sm≥768、md≥992），
  // 只写 :span="6" 就等于全屏固定 25%——375px 手机上也是每行 4 张挤成一条。
  // 现在 xs/sm=12（<992px 每行 2 张）、md=6（≥992px 每行 4 张），与 wms 原实现一致。
  margin-bottom: var(--yc-space-6);
}

.yc-kpi-grid__card {
  // nos 的「查看」钻取链接用绝对定位挂在卡片右上角，这里是它的定位上下文。
  position: relative;
  text-align: center;

  &--link {
    cursor: pointer;
  }
}

.yc-kpi-grid__value {
  font-size: var(--yc-font-2xl);
  font-weight: 600;
  line-height: 1.4;
}

.yc-kpi-grid__label {
  margin-top: var(--yc-space-1);
  color: var(--yc-text-secondary);
  font-size: var(--yc-font-sm);
}
</style>
